import { Paper, Popper, Typography } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { useTheme } from "../../../context/ThemeContext";

// the card sits off the dot by the radius of the ring around it and a little
// more. Popper.js keeps it inside the viewport: it slides the card sideways
// near the left or right edge (preventOverflow, 8px short of the edge) and
// moves it under the dot when there is no room above (flip, on by default)
const MODIFIERS = [
  { name: "offset", options: { offset: [0, 16] } },
  { name: "preventOverflow", options: { padding: 8 } },
];

/**
 * The floating card for a hovered scatter dot, centred above the chart.js
 * caret, or under it near the top of the viewport. `x`/`y` are the caret's
 * CSS pixels on `canvas`.
 */
export const ScatterTooltip = ({
  canvas,
  x,
  y,
  title,
  children,
}: {
  canvas: HTMLCanvasElement;
  x: number;
  y: number;
  title: string;
  children: ReactNode;
}) => {
  const { theme } = useTheme();
  // a zero-size box at the caret, measured from the canvas each time Popper.js
  // positions the card, so the card follows the dot when the page scrolls
  const anchor = useMemo(
    () => ({
      getBoundingClientRect: () => {
        const { left, top } = canvas.getBoundingClientRect();
        return new DOMRect(left + x, top + y, 0, 0);
      },
      contextElement: canvas,
    }),
    [canvas, x, y],
  );
  return (
    <Popper
      open
      anchorEl={anchor}
      placement="top"
      modifiers={MODIFIERS}
      sx={{ pointerEvents: "none", zIndex: theme.zIndex.tooltip }}
    >
      <Paper
        elevation={8}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2.5,
          px: 1.625,
          py: 1.375,
          whiteSpace: "nowrap",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {children}
      </Paper>
    </Popper>
  );
};
